import React from 'react'
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap'

/*
    Public, unauthenticated account-deletion request page.

    This screen is mounted in src/index.js OUTSIDE the AuthorizedUser guard and the
    API-availability gate, so a logged-out visitor (including a Google Play reviewer)
    can always reach it. It talks to the public deletion endpoint with a plain fetch:
    no auth header, no cookies, no shared axios instance.

    The endpoint URL is hard-coded to the deployed production API on purpose. This is a
    public compliance page that must target the live endpoint regardless of build env,
    and the endpoint is a bare path with no /v1 prefix, so it does not fit the
    env-switched generator in src/constants/api-endpoints.
*/
const ENDPOINT_URL = 'https://api.aw.dsha.info/api/account-deletion-request'
const CONTACT_EMAIL = 'me@caseykerr.com'

//  Limits mirror the server contract so we validate before sending.
const LIMITS = {
    fullName: 200,
    accountEmail: 254,
    accountIdentifier: 200,
    reason: 2000,
}

//  Standard, permissive email check. We rely on the server (400) as the backstop.
const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const EMPTY_FORM = {
    fullName: '',
    accountEmail: '',
    accountIdentifier: '',
    reason: '',
    company: '', //  honeypot, must stay empty for real users
}

const validate = (form) => {
    const errors = {}

    const fullName = form.fullName.trim()
    if (!fullName) {
        errors.fullName = 'Please enter your full name.'
    } else if (fullName.length > LIMITS.fullName) {
        errors.fullName = `Please keep your name under ${LIMITS.fullName} characters.`
    }

    const accountEmail = form.accountEmail.trim()
    if (!accountEmail) {
        errors.accountEmail = 'Please enter the email address on your account.'
    } else if (accountEmail.length > LIMITS.accountEmail || !isEmail(accountEmail)) {
        errors.accountEmail = 'Please enter a valid email address.'
    }

    if (form.accountIdentifier.trim().length > LIMITS.accountIdentifier) {
        errors.accountIdentifier = `Please keep this under ${LIMITS.accountIdentifier} characters.`
    }

    if (form.reason.trim().length > LIMITS.reason) {
        errors.reason = `Please keep your reason under ${LIMITS.reason} characters.`
    }

    return errors
}

const ContactLine = () => (
    <span>
        You can also request deletion by emailing{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
    </span>
)

const SuccessPanel = () => (
    <Alert variant='success' role='status'>
        <Alert.Heading as='h2' style={{ fontSize: '1.25rem' }}>Request received</Alert.Heading>
        <p className='mb-2'>
            We have received your request and will process it within 30 days.
        </p>
        <p className='mb-0'>
            If you have any questions, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
    </Alert>
)

const DeleteAccountScreen = () => {
    const [form, setForm] = React.useState(EMPTY_FORM)
    const [errors, setErrors] = React.useState({})
    //  status: 'idle' | 'submitting' | 'success' | 'error'
    const [status, setStatus] = React.useState('idle')
    const [errorMessage, setErrorMessage] = React.useState('')

    React.useEffect(() => {
        document.title = 'Delete your DSHA account | Applied Wellness'
    }, [])

    const handleChange = (field) => (event) => {
        const { value } = event.target
        setForm((current) => ({ ...current, [field]: value }))
        setErrors((current) => {
            if (!current[field]) {
                return current
            }
            const next = { ...current }
            delete next[field]
            return next
        })
    }

    const handleSubmit = async (event) => {
        event.preventDefault()

        const validationErrors = validate(form)
        setErrors(validationErrors)
        if (Object.keys(validationErrors).length > 0) {
            return
        }

        setStatus('submitting')
        setErrorMessage('')

        const payload = {
            fullName: form.fullName.trim(),
            accountEmail: form.accountEmail.trim(),
            accountIdentifier: form.accountIdentifier.trim(),
            reason: form.reason.trim(),
            company: form.company,
        }

        try {
            const response = await fetch(ENDPOINT_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            })

            if (response.status === 202) {
                setStatus('success')
                return
            }

            if (response.status === 400) {
                setErrorMessage('Please check your entries and try again.')
            } else if (response.status === 429) {
                setErrorMessage('We have received several requests from you recently. Please try again in a few minutes.')
            } else {
                setErrorMessage('Something went wrong on our end. Please try again in a few minutes.')
            }
            setStatus('error')
        } catch (requestError) {
            setErrorMessage('We could not reach our servers. Please check your connection and try again.')
            setStatus('error')
        }
    }

    const isSubmitting = status === 'submitting'

    return (
        <Container className='py-4 py-md-5' style={{ maxWidth: '640px', color: '#212529' }}>
            <div className='text-center mb-4'>
                <h1 style={{ fontSize: '1.5rem', marginBottom: '0.25rem', color: '#007bff' }}>DSHA</h1>
                <div className='text-muted'>Applied Wellness</div>
            </div>

            <Card>
                <Card.Body className='p-4'>
                    <h2 style={{ fontSize: '1.5rem' }}>Delete your DSHA account</h2>

                    <p>
                        DSHA is the student wellness app from Applied Wellness. Use this page to
                        request deletion of your DSHA account and the personal data associated with it.
                    </p>
                    <p>
                        When you submit this form, our team receives a request to permanently delete
                        your account and its associated data. We process deletion requests within 30 days.
                    </p>
                    <p className='mb-4'>
                        <ContactLine />
                    </p>

                    {status === 'success' ? (
                        <SuccessPanel />
                    ) : (
                        <React.Fragment>
                            {status === 'error' && (
                                <Alert variant='danger' role='alert'>
                                    <div>{errorMessage}</div>
                                    <div className='mt-2'>
                                        You can try again below, or email{' '}
                                        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                                    </div>
                                </Alert>
                            )}

                            <Form noValidate onSubmit={handleSubmit}>
                                <Form.Group controlId='delete-account-full-name'>
                                    <Form.Label>
                                        Full name <span aria-hidden='true'>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type='text'
                                        value={form.fullName}
                                        onChange={handleChange('fullName')}
                                        isInvalid={Boolean(errors.fullName)}
                                        maxLength={LIMITS.fullName}
                                        required
                                        aria-required='true'
                                        autoComplete='name'
                                        disabled={isSubmitting}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {errors.fullName}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId='delete-account-email'>
                                    <Form.Label>
                                        Account email <span aria-hidden='true'>*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type='email'
                                        value={form.accountEmail}
                                        onChange={handleChange('accountEmail')}
                                        isInvalid={Boolean(errors.accountEmail)}
                                        maxLength={LIMITS.accountEmail}
                                        required
                                        aria-required='true'
                                        autoComplete='email'
                                        disabled={isSubmitting}
                                    />
                                    <Form.Text className='text-muted'>
                                        Use the email address associated with your DSHA account.
                                    </Form.Text>
                                    <Form.Control.Feedback type='invalid'>
                                        {errors.accountEmail}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId='delete-account-identifier'>
                                    <Form.Label>Username or member ID (optional)</Form.Label>
                                    <Form.Control
                                        type='text'
                                        value={form.accountIdentifier}
                                        onChange={handleChange('accountIdentifier')}
                                        isInvalid={Boolean(errors.accountIdentifier)}
                                        maxLength={LIMITS.accountIdentifier}
                                        disabled={isSubmitting}
                                    />
                                    <Form.Text className='text-muted'>
                                        This helps us find your account faster if you have it.
                                    </Form.Text>
                                    <Form.Control.Feedback type='invalid'>
                                        {errors.accountIdentifier}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group controlId='delete-account-reason'>
                                    <Form.Label>Reason for deletion (optional)</Form.Label>
                                    <Form.Control
                                        as='textarea'
                                        rows={4}
                                        value={form.reason}
                                        onChange={handleChange('reason')}
                                        isInvalid={Boolean(errors.reason)}
                                        maxLength={LIMITS.reason}
                                        disabled={isSubmitting}
                                    />
                                    <Form.Control.Feedback type='invalid'>
                                        {errors.reason}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {/*
                                    Honeypot. Hidden from real users but labeled like a real field so
                                    bots fill it. Kept out of the tab order and hidden from assistive
                                    tech. Always sent to the server, empty for real users.
                                */}
                                <div
                                    aria-hidden='true'
                                    style={{
                                        position: 'absolute',
                                        left: '-9999px',
                                        width: '1px',
                                        height: '1px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <label htmlFor='delete-account-company'>Company</label>
                                    <input
                                        id='delete-account-company'
                                        name='company'
                                        type='text'
                                        tabIndex={-1}
                                        autoComplete='off'
                                        value={form.company}
                                        onChange={handleChange('company')}
                                    />
                                </div>

                                <Button
                                    type='submit'
                                    variant='primary'
                                    disabled={isSubmitting}
                                    className='mt-2'
                                >
                                    {isSubmitting ? (
                                        <React.Fragment>
                                            <Spinner
                                                as='span'
                                                animation='border'
                                                size='sm'
                                                role='status'
                                                aria-hidden='true'
                                                className='mr-2'
                                            />
                                            Submitting your request...
                                        </React.Fragment>
                                    ) : (
                                        'Request account deletion'
                                    )}
                                </Button>
                            </Form>
                        </React.Fragment>
                    )}
                </Card.Body>
            </Card>

            <p className='text-muted text-center mt-3 mb-0' style={{ fontSize: '0.875rem' }}>
                DSHA is a product of Applied Wellness.
            </p>
        </Container>
    )
}

export default DeleteAccountScreen
